import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type {
  BillingInvoice,
  UpdateBillingContactPayload,
} from '@gokrew/models/billing';
import { useAuth } from '@/context/AuthContext';
import {
  downloadBillingInvoicePdf,
  getBillingAccess,
  getBillingInvoices,
  getBillingOverview,
  sendBillingInvoice,
  startPaymentMethodCheckout,
  updateBillingContact,
} from '@/api/billing';
import { downloadFile } from '@/utils/performance';

type QC = ReturnType<typeof useQueryClient>;

/** The overview and the invoice list move together (status, emailed date). */
export const invalidateBilling = (qc: QC, companyId: string) => {
  for (const key of [QueryKeys.BillingOverview, QueryKeys.BillingInvoices]) {
    qc.invalidateQueries({ queryKey: [key, companyId] });
  }
};

/**
 * Whether billing applies to the company: the Billing page, its Organization
 * card and its route only exist while `visible`. A failed read (e.g. a
 * backend without billing yet) counts as not visible.
 */
export const useBillingAccess = () => {
  const { companyId } = useAuth();
  const query = useQuery({
    queryKey: [QueryKeys.BillingAccess, companyId],
    queryFn: () => getBillingAccess(companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    select: (res) => res.data,
  });
  return {
    ...query,
    access: query.data,
    visible: query.data?.visible === true,
    /** False until the first answer (or failure) for this company. */
    isResolved: !query.isPending,
  };
};

export const useBillingOverview = (enabled = true) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.BillingOverview, companyId],
    queryFn: () => getBillingOverview(companyId!),
    enabled: !!companyId && enabled,
    select: (res) => res.data,
  });
};

export const useBillingInvoices = (
  page: number,
  limit: number,
  enabled = true
) => {
  const { companyId } = useAuth();
  return useQuery({
    queryKey: [QueryKeys.BillingInvoices, companyId, page, limit],
    queryFn: () => getBillingInvoices(companyId!, { page, limit }),
    enabled: !!companyId && enabled,
    placeholderData: keepPreviousData,
    select: (res) => res.data,
  });
};

export const useUpdateBillingContact = () => {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Billing);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBillingContactPayload) =>
      updateBillingContact(companyId!, payload),
    onSuccess: () => {
      invalidateBilling(qc, companyId!);
      toast.success(t('contact.saved'));
    },
  });
};

/** Sends the browser to Stripe Checkout to save a card. */
export const useStartPaymentCheckout = () => {
  const { companyId } = useAuth();
  return useMutation({
    mutationFn: () => startPaymentMethodCheckout(companyId!),
    onSuccess: ({ data }) => window.location.assign(data.url),
  });
};

export const useDownloadBillingInvoice = () => {
  const { companyId } = useAuth();
  return useMutation({
    mutationFn: (invoice: Pick<BillingInvoice, 'id' | 'number'>) =>
      downloadBillingInvoicePdf(companyId!, invoice),
    onSuccess: downloadFile,
  });
};

export const useSendBillingInvoice = () => {
  const { companyId } = useAuth();
  const { t } = useTranslation(TranslationNS.Billing);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      sendBillingInvoice(companyId!, invoiceId),
    onSuccess: ({ data }) => {
      invalidateBilling(qc, companyId!);
      toast.success(
        data.emailedTo
          ? t('invoices.sent_to', { email: data.emailedTo })
          : t('invoices.sent')
      );
    },
  });
};
